import { BLEND_MODES, Container, filters, Ticker, utils } from "pixi.js"
import { SoftenedGraphics } from "./softened_graphics"
import { findRendererApp } from "./renderer_app"

/** How long each half of a change's fade takes: the new shade fading in, then the old fading out. */
export const SHADE_FADE_MS = 260

/** A blend mode id of our own: each channel the larger of what is there and what lands (registered below). */
const MAX_BLEND = 64 as BLEND_MODES
let maxBlendReady: boolean | undefined

/**
 * The darker-of-two blend, registered once; undefined where WebGL offers no MAX. It must be known in two
 * places: the renderer's table of GL blend functions, and pixi's premultiplied-alpha table, which every
 * batched sprite's mode is looked up through first -- missing there, a sprite asked the renderer for an
 * undefined mode, the batch threw mid-flush, and every frame after it failed.
 */
const maxBlend = (): BLEND_MODES | undefined => {
    const renderer = findRendererApp()?.renderer as any
    const gl: WebGLRenderingContext | undefined = renderer?.gl
    if (maxBlendReady === undefined) {
        const max = !gl ? undefined : renderer.context?.webGLVersion === 2 ? (gl as any).MAX : gl.getExtension("EXT_blend_minmax")?.MAX_EXT
        maxBlendReady = max !== undefined && !!renderer?.state?.blendModes && !!utils.premultiplyBlendMode?.[0] && !!utils.premultiplyBlendMode?.[1]
        if (maxBlendReady) {
            renderer.state.blendModes[MAX_BLEND] = [gl.ONE, gl.ONE, gl.ONE, gl.ONE, max, max]
            utils.premultiplyBlendMode[0][MAX_BLEND] = MAX_BLEND
            utils.premultiplyBlendMode[1][MAX_BLEND] = MAX_BLEND
        }
    }
    // A lost and restored context rebuilds the renderer's table: never hand out a mode it no longer knows.
    return maxBlendReady && renderer?.state?.blendModes?.[MAX_BLEND] ? MAX_BLEND : undefined
}

/**
 * A layer's softened groups, swapped whole, and faded. A group drawn afresh stays hidden until its bake
 * has landed, and the groups it replaces stay on screen until then: a tree felled re-draws the forest's
 * shadow, and destroying the old bake before the new one landed made the shade flicker off.
 *
 * Then the change fades, without the dip a crossfade of two multiplied pictures would give (half of one
 * times half of the other is lighter than either). For the fade the groups are drawn into one buffer
 * that is multiplied once (a filter on the layer, only while fading), each pixel keeping the DARKER of
 * the old and the new (the GPU's MAX: every group of a layer has the same shade colour, so the darker shade
 * is the larger coverage): first the new fade in (shade that appears), then the old fade out (shade that
 * goes). Where nothing changed both are the same, so the shade stays exactly as it was -- soft edges and
 * dappled leaf shade too, which drawn one over the other briefly deepened (a felled tree darkened the
 * shade round it).
 */
export class BakedGroups<T extends { key: string; graphics: SoftenedGraphics }> {
    groups: T[] = []
    /** Drawn, not baked yet: hidden. */
    private pending = new Set<SoftenedGraphics>()
    /** Replaced, still on screen until the pending ones land (and then fading out). */
    private retiring: SoftenedGraphics[] = []
    private fade?: { arriving: SoftenedGraphics[]; leaving: SoftenedGraphics[]; phase: "in" | "out"; start: number }
    private buffer?: InstanceType<typeof filters.AlphaFilter>

    constructor(private readonly layer: Container) {}

    /**
     * Replace the groups with `next`: those in it already kept their place; `fresh` (also in it) are new,
     * added hidden and softened here. Groups no longer wanted go now if never shown, else at the swap.
     */
    replace(next: T[], fresh: SoftenedGraphics[]): void {
        this.finishFade()
        const wanted = new Set(next.map(group => group.graphics))
        this.groups.forEach(({ graphics }) => {
            if (wanted.has(graphics)) return
            if (this.pending.delete(graphics)) this.discard(graphics)
            else this.retiring.push(graphics)
        })
        this.groups = next
        fresh.forEach(graphics => {
            graphics.visible = false
            this.pending.add(graphics)
            this.layer.addChild(graphics)
        })
        fresh.forEach(graphics => graphics.soften(() => {
            if (!this.pending.delete(graphics)) return
            if (!this.pending.size) this.swap()
        }))
        if (!this.pending.size) this.swap()
    }

    /** Whether a fade is running (for tests). */
    get fading(): boolean { return !!this.fade }

    private swap(): void {
        const arriving = this.groups.map(group => group.graphics).filter(graphics => !graphics.visible)
        const leaving = this.retiring
        this.retiring = []
        // Headless (no renderer, no frames to fade over): swap at once.
        if (!findRendererApp() || typeof requestAnimationFrame === "undefined" || (!arriving.length && !leaving.length)) {
            arriving.forEach(graphics => { graphics.visible = true })
            leaving.forEach(graphics => this.discard(graphics))
            return
        }
        this.buffer ??= Object.assign(new filters.AlphaFilter(1), { blendMode: BLEND_MODES.MULTIPLY })
        this.layer.filters = [this.buffer]
        const landing = maxBlend() ?? BLEND_MODES.NORMAL
        this.groups.forEach(({ graphics }) => graphics.setLanding(landing))
        leaving.forEach(graphics => graphics.setLanding(landing))
        arriving.forEach(graphics => { graphics.visible = true; graphics.alpha = 0 })
        // The old under the new while the new fades in.
        leaving.forEach(graphics => this.layer.addChildAt(graphics, 0))
        this.fade = { arriving, leaving, phase: arriving.length ? "in" : "out", start: performance.now() }
        if (this.fade.phase === "out") this.leaveOnTop()
        Ticker.shared.add(this.tick)
    }

    private leaveOnTop(): void {
        this.fade.arriving.forEach(graphics => { graphics.alpha = 1 })
        // The old over the new while it fades out.
        this.fade.leaving.forEach(graphics => this.layer.addChild(graphics))
    }

    private tick = (): void => {
        const fade = this.fade
        if (!fade) return
        const t = Math.min(1, (performance.now() - fade.start) / SHADE_FADE_MS)
        const eased = t * t * (3 - 2 * t)
        if (fade.phase === "in") {
            fade.arriving.forEach(graphics => { if (!graphics.destroyed) graphics.alpha = eased })
            if (t < 1) return
            if (!fade.leaving.length) { this.finishFade(); return }
            fade.phase = "out"
            fade.start = performance.now()
            this.leaveOnTop()
            return
        }
        fade.leaving.forEach(graphics => { if (!graphics.destroyed) graphics.alpha = 1 - eased })
        if (t >= 1) this.finishFade()
    }

    /** Ends a fade where it would end: the new at full, the old gone, the layer multiplied group by group again. */
    private finishFade(): void {
        const fade = this.fade
        if (!fade) return
        this.fade = undefined
        Ticker.shared.remove(this.tick)
        fade.arriving.forEach(graphics => { if (!graphics.destroyed) graphics.alpha = 1 })
        fade.leaving.forEach(graphics => this.discard(graphics))
        this.layer.filters = null
        this.groups.forEach(({ graphics }) => { if (!graphics.destroyed) graphics.setLanding() })
    }

    private discard(graphics: SoftenedGraphics): void {
        if (graphics.destroyed) return
        graphics.parent?.removeChild(graphics)
        graphics.destroy()
    }

    clear(): void {
        this.finishFade()
        this.groups.forEach(({ graphics }) => this.discard(graphics))
        this.retiring.forEach(graphics => this.discard(graphics))
        this.groups = []
        this.retiring = []
        this.pending.clear()
        this.buffer?.destroy()
        this.buffer = undefined
    }
}
